import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Typography,
} from '@mui/material'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { BaseService } from '../../services/BaseService'
import { isDobValid } from '../../utils/dobValidation'

const DobModal = ({ open, userId, onSuccess, initialDob }) => {
  const [selectedDate, setSelectedDate] = useState(() => {
    if (isDobValid(initialDob)) {
      return dayjs(initialDob)
    }
    return null
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isDobValid(initialDob)) {
      setSelectedDate(dayjs(initialDob))
    }
  }, [initialDob])

  const maxValidDate = dayjs().subtract(10, 'year') // Minimal usia 10 tahun
  const minValidDate = dayjs().subtract(80, 'year') // Maksimal usia 80 tahun

  const calculatedAge = useMemo(() => {
    if (selectedDate && selectedDate.isValid()) {
      const diff = dayjs().diff(selectedDate, 'year')
      if (diff >= 10 && diff <= 100) return diff
    }
    return null
  }, [selectedDate])

  const handleSubmit = async () => {
    if (!selectedDate || !selectedDate.isValid()) {
      setError('Silakan pilih tanggal lahir Anda.')
      return
    }

    if (selectedDate.isAfter(maxValidDate)) {
      setError('Usia minimal peserta untuk tes IST adalah 15 tahun.')
      return
    }

    if (selectedDate.isBefore(minValidDate)) {
      setError('Tanggal lahir tidak valid. Silakan periksa kembali tahun lahir Anda.')
      return
    }

    const formattedDob = selectedDate.format('YYYY-MM-DD')

    setIsLoading(true)
    setError(null)

    try {
      const service = new BaseService('/m_update_dob')
      const res = await service.updateDob({
        dob: formattedDob,
        id_user: userId,
      })

      if (res?.status === 'gagal' || res?.data?.status === 'gagal') {
        setError(res?.message || res?.data?.message || 'Gagal menyimpan tanggal lahir.')
        return
      }

      const successMessage =
        res?.message || res?.data?.message || 'Tanggal lahir berhasil disimpan!'
      if (onSuccess) {
        onSuccess(formattedDob, successMessage)
      }
    } catch (err) {
      console.error('Error update dob:', err)
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Gagal memperbarui tanggal lahir. Periksa koneksi Anda.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return
      }}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '20px',
          p: { xs: 1, sm: 1.5 },
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 2, px: 2.5, textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
          <Avatar
            sx={{
              width: 58,
              height: 58,
              background: 'linear-gradient(135deg, #60B0E3 0%, #9AA5EA 100%)',
              boxShadow: '0 8px 20px rgba(96, 176, 227, 0.35)',
            }}
          >
            <CalendarMonthRoundedIcon sx={{ fontSize: 32, color: '#fff' }} />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Konfirmasi Tanggal Lahir
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Wajib diisi sebelum memulai modul <strong>IST</strong>
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 1.5 }}>
        {/* Notice Info Card */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.2,
            backgroundColor: '#F0F6FF',
            border: '1px solid #D0E2FF',
            borderRadius: '12px',
            p: 1.5,
            mb: 2.5,
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#3B82F6', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
          <Typography variant="body2" color="#1E3A8A" sx={{ fontSize: '12px', lineHeight: 1.5 }}>
            Sesuai standar psikotes IST, perhitungan norma IQ membutuhkan tanggal lahir akurat
            sesuai KTP/identitas resmi Anda.
          </Typography>
        </Box>

        {error && (
          <Fade in={!!error}>
            <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
              {error}
            </Alert>
          </Fade>
        )}

        {/* Date Picker Input */}
        <Box>
          <Typography variant="caption" fontWeight={600} color="text.primary" display="block" mb={0.8}>
            Pilih Tanggal Lahir Anda:
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="YYYY-MM-DD"
              value={selectedDate}
              maxDate={maxValidDate}
              minDate={minValidDate}
              openTo="year"
              views={['year', 'month', 'day']}
              onChange={(newVal) => {
                setSelectedDate(newVal)
                setError(null)
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'medium',
                  placeholder: 'YYYY-MM-DD',
                  error: !!error,
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: '#FAFAFA',
                      '&:hover': {
                        backgroundColor: '#FFFFFF',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#FFFFFF',
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* Live Age Preview */}
        {calculatedAge !== null && (
          <Fade in={calculatedAge !== null}>
            <Box mt={1.5} display="flex" justifyContent="center">
              <Chip
                icon={<CheckCircleOutlineRoundedIcon sx={{ fontSize: '16px !important' }} />}
                label={`Usia terhitung: ${calculatedAge} Tahun`}
                size="small"
                sx={{
                  backgroundColor: '#ECFDF5',
                  color: '#065F46',
                  fontWeight: 600,
                  fontSize: '12px',
                  borderRadius: '8px',
                  border: '1px solid #A7F3D0',
                  py: 1.8,
                  px: 0.5,
                }}
              />
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
        <Button
          variant="contained"
          fullWidth
          disabled={isLoading || !selectedDate}
          onClick={handleSubmit}
          sx={{
            fontWeight: 700,
            py: 1.3,
            fontSize: '14px',
            borderRadius: '12px',
            textTransform: 'none',
            background: 'linear-gradient(135deg, #60B0E3 0%, #4E9AD6 100%)',
            boxShadow: '0 4px 14px rgba(96, 176, 227, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4E9AD6 0%, #3B82C4 100%)',
              boxShadow: '0 6px 18px rgba(96, 176, 227, 0.5)',
            },
            '&.Mui-disabled': {
              background: '#E0E0E0',
              color: '#9E9E9E',
            },
          }}
        >
          {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Simpan & Lanjutkan'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

DobModal.propTypes = {
  open: PropTypes.bool.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
  initialDob: PropTypes.string,
}

export default DobModal
